import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { useGroupUuid } from "../hooks/use-group-uuid";
import {
  groupKitChatControllerCreateChat,
  groupKitControllerCreateKit,
} from "../lib/gen-api";
import { queryKeys } from "../lib/query-keys";

type CreateInterviewOptions = {
  expertUuid: string;
  name?: string;
  scriptUuid?: string;
  description?: string;
};

export function useCreateInterviewAction() {
  const groupUuid = useGroupUuid();
  const [isCreatingInterview, setIsCreatingInterview] = useState(false);
  const queryClient = useQueryClient();

  async function create({
    expertUuid,
    name,
    scriptUuid,
    description,
  }: CreateInterviewOptions): Promise<{
    kitUuid: string;
    chatUuid: string;
  }> {
    setIsCreatingInterview(true);

    try {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      // 1. Create Interview Kit
      const interviewKit = await groupKitControllerCreateKit({
        path: {
          groupUuid,
        },
        body: {
          name: name || "New Interview",
          type: "interview",
          expert_uuids: [expertUuid],
          script_uuids: scriptUuid ? [scriptUuid] : undefined,
          description,
        },
      }).then((res) => res.data);

      if (!interviewKit) {
        throw new Error("Failed to create interview");
      }

      // 2. Create the single chat artifact
      const chat = await groupKitChatControllerCreateChat({
        path: {
          kitUuid: interviewKit.uuid,
          groupUuid: groupUuid!,
        },
        body: {
          name: "Interview",
          expertUuids: [expertUuid],
        },
      }).then((res) => res.data);

      if (!chat) {
        throw new Error("Failed to create chat");
      }

      // 3. Invalidate queries to refresh the UI
      queryClient.invalidateQueries({
        queryKey: queryKeys.kits(groupUuid!, { type: "interview" }),
      });

      return {
        kitUuid: interviewKit.uuid,
        chatUuid: chat.uuid,
      };
    } catch (error) {
      console.error(error);
      toast.error("Failed to create interview", {
        description: error instanceof Error ? error.message : "",
      });
      throw error;
    } finally {
      setIsCreatingInterview(false);
    }
  }

  return {
    create,
    isCreatingInterview,
  };
}
